import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { deptLabel } from "../lib/labels";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PageHeader from "../components/system/PageHeader";
import Card from "../components/system/Card";
import Button from "../components/system/Button";
import StatusChip from "../components/system/StatusChip";
import {
  Bell,
  BellOff,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Mail,
  MailOpen,
  RefreshCw,
  X,
} from "lucide-react";
import {
  isPushSupported,
  currentPermission,
  getExistingSubscription,
  enablePushNotifications,
  reconnectPushNotifications,
  disablePushNotifications,
  sendTestPush,
} from "../lib/push";

const URGENCY_LABEL = {
  expired: "Expired",
  urgent: "Urgent",
  attention: "Needs attention",
};

const URGENCY_TONE = {
  expired: "expired",
  urgent: "urgent",
  attention: "soon",
};

// Show this many newest unread on first paint; expand for the rest.
const UNREAD_INITIAL_LIMIT = 50;
// Cap the read-history panel; older entries remain in DB.
const READ_HISTORY_LIMIT = 100;

/* ---------------- single row ---------------- */
function NotificationRow({ n, selected, onToggleSelect, selectionMode }) {
  const meta = n.metadata || {};
  return (
    <label
      className={`list-row items-start cursor-pointer ${n.read ? "opacity-70" : ""} ${
        selected ? "bg-secondary/60" : ""
      }`}
      data-testid={`notification-row-${n.id}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(n.id)}
        className="w-5 h-5 mt-1 accent-brand cursor-pointer shrink-0"
        data-testid={`notification-checkbox-${n.id}`}
        aria-label={`Select notification: ${n.title}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {n.urgency && URGENCY_LABEL[n.urgency] && (
            <StatusChip
              tone={URGENCY_TONE[n.urgency] || "neutral"}
              data-testid={`notification-urgency-${n.urgency}`}
            >
              {URGENCY_LABEL[n.urgency]}
            </StatusChip>
          )}
          {!n.read && (
            <span
              aria-hidden
              title="Unread"
              className="inline-block w-2 h-2 rounded-full bg-brand"
              data-testid={`unread-dot-${n.id}`}
            />
          )}
          <div className="font-display font-semibold">{n.title}</div>
        </div>
        <div className="text-sm mt-1">{n.body}</div>
        {(meta.product_name || meta.quantity || meta.expiration_date) && (
          <div className="mt-2 font-mono text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
            {meta.product_name && <span>Product: {meta.product_name}</span>}
            {meta.quantity !== undefined && meta.quantity !== null && (
              <span>Qty: {meta.quantity}</span>
            )}
            {meta.expiration_date && <span>Exp: {meta.expiration_date}</span>}
            {meta.department_name && <span>Dept: {deptLabel(meta.department_name)}</span>}
            {meta.store_name && <span>Store: {meta.store_name}</span>}
          </div>
        )}
        <div className="data-sku mt-2">{new Date(n.created_at).toLocaleString()}</div>
      </div>
    </label>
  );
}

/* ---------------- page ---------------- */
export default function NotificationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Managers live in the admin shell (sidebar + routes under
  // /admin/*), so they need the admin-side preferences route too —
  // otherwise the link below would deep-link them into the employee
  // shell and ProtectedRoute would bounce them back to /admin.
  const isAdminShell = isAdmin || user?.role === "manager";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [selected, setSelected] = useState(() => new Set());
  const [showAllUnread, setShowAllUnread] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Web push
  const pushSupported = isPushSupported();
  const [permission, setPermission] = useState(pushSupported ? currentPermission() : "unsupported");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported) return;
    getExistingSubscription()
      .then((sub) => setPushSubscribed(!!sub))
      .catch(() => setPushSubscribed(false));
  }, [pushSupported]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await enablePushNotifications();
      setPushSubscribed(true);
      setPermission(currentPermission());
      toast.success("Browser push enabled for this device");
    } catch (e) {
      toast.error(e.message || "Failed to enable push");
      setPermission(currentPermission());
    } finally {
      setPushBusy(false);
    }
  };

  const reconnectPush = async () => {
    setPushBusy(true);
    try {
      await reconnectPushNotifications();
      setPushSubscribed(true);
      // Immediately fire a self-test so the user sees confirmation.
      try {
        const r = await sendTestPush();
        if (r.sent > 0) {
          toast.success("Reconnected — test notification sent to your device");
        } else {
          toast.success("Reconnected — but the test push did not deliver");
        }
      } catch {
        toast.success("Reconnected push subscription");
      }
      load();
    } catch (e) {
      toast.error(e.message || "Could not reconnect push notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      await disablePushNotifications();
      setPushSubscribed(false);
      toast.success("Browser push disabled for this device");
    } catch (e) {
      toast.error(e.message || "Failed to disable push");
    } finally {
      setPushBusy(false);
    }
  };

  const sendTest = async () => {
    setPushBusy(true);
    try {
      const r = await sendTestPush();
      if (r.sent > 0) {
        toast.success("Test notification sent — check your lock screen");
      } else if (r.subscriptions === 0) {
        // Reconcile UI: server has no subscription for this user/device,
        // so reflect that immediately so the Reconnect button surfaces.
        setPushSubscribed(false);
        toast.error("No push subscription on this device. Tap 'Reconnect notifications' to register.");
      } else {
        toast.error("Push could not be delivered. Check Android app notification settings.");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPushBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* selection helpers */
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  /* bulk action: set read=true/false on the currently-selected ids */
  const setReadForSelected = async (readValue) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    // Optimistic local flip.
    setItems((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, read: readValue } : x)));
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/bulk-update-read", {
        ids,
        read: readValue,
      });
      toast.success(
        `Marked ${data.updated} as ${readValue ? "read" : "unread"}`
      );
      clearSelection();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  const markAllRead = async () => {
    const unreadCount = unread.length;
    if (unreadCount === 0) return;
    setBusy(true);
    // Optimistic.
    setItems((prev) => prev.map((x) => (x.read ? x : { ...x, read: true })));
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(`Marked ${data.marked} as read`);
      clearSelection();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/notifications/generate-expiration-alerts");
      toast.success(
        `Generated ${data.generated} alert${data.generated === 1 ? "" : "s"} · scanned ${data.scanned} batches`
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setGenerating(false);
    }
  };

  /* derived buckets */
  const { unread, read } = useMemo(() => {
    const u = [];
    const r = [];
    for (const n of items) (n.read ? r : u).push(n);
    return { unread: u, read: r };
  }, [items]);

  const visibleUnread = showAllUnread ? unread : unread.slice(0, UNREAD_INITIAL_LIMIT);
  const visibleRead = read.slice(0, READ_HISTORY_LIMIT);
  const hiddenUnread = unread.length - visibleUnread.length;
  const hiddenRead = Math.max(0, read.length - visibleRead.length);

  /* selection summary */
  const selectedCount = selected.size;
  const selectedHasUnread = useMemo(
    () => items.some((n) => selected.has(n.id) && !n.read),
    [items, selected]
  );
  const selectedHasRead = useMemo(
    () => items.some((n) => selected.has(n.id) && n.read),
    [items, selected]
  );

  /* select-all-visible helper */
  const allVisibleIds = useMemo(
    () => [...visibleUnread, ...(historyOpen ? visibleRead : [])].map((n) => n.id),
    [visibleUnread, visibleRead, historyOpen]
  );
  const allVisibleSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const next = new Set(selected);
      for (const id of allVisibleIds) next.delete(id);
      setSelected(next);
    } else {
      const next = new Set(selected);
      for (const id of allVisibleIds) next.add(id);
      setSelected(next);
    }
  };

  return (
    <div data-testid="notifications-page" className="p-4 sm:p-0 pb-32 sm:pb-24">
      {/* ---------------- Header ---------------- */}
      <PageHeader
        eyebrow="Inbox"
        title={
          <span className="inline-flex items-center gap-3">
            <Bell size={28} strokeWidth={2.5} />
            Notifications
          </span>
        }
        titleSuffix={
          unread.length > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-chip bg-brand text-white text-xs font-mono"
            >
              {unread.length}
            </span>
          )
        }
        actions={
          <>
            {isAdmin && (
              <Button
                tier="primary"
                onClick={generate}
                disabled={generating}
                data-testid="notifications-generate-expiration-btn"
              >
                {generating ? "Generating…" : "Generate alerts"}
              </Button>
            )}
            <Button
              tier="secondary"
              onClick={load}
              disabled={loading}
              data-testid="notifications-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
            {pushSupported && permission === "granted" && pushSubscribed && (
              <>
                <Button
                  tier="secondary"
                  onClick={sendTest}
                  disabled={pushBusy}
                  data-testid="push-test-btn"
                  title="Send a one-off self-test push to confirm Android delivery"
                >
                  <Bell size={14} />
                  Send test
                </Button>
                <Button
                  tier="secondary"
                  onClick={disablePush}
                  disabled={pushBusy}
                  data-testid="push-disable-btn"
                  title="Stop receiving browser push on this device"
                >
                  <BellOff size={14} />
                  Disable push
                </Button>
              </>
            )}
            {pushSupported && permission === "granted" && !pushSubscribed && (
              <Button
                tier="primary"
                onClick={reconnectPush}
                disabled={pushBusy}
                data-testid="push-reconnect-btn"
                title="Re-register this device's push subscription"
              >
                <Bell size={14} />
                {pushBusy ? "Reconnecting…" : "Reconnect"}
              </Button>
            )}
            {pushSupported && permission === "default" && (
              <Button
                tier="secondary"
                onClick={enablePush}
                disabled={pushBusy}
                data-testid="push-enable-btn"
                title="Turn on real browser push"
              >
                <Bell size={14} />
                {pushBusy ? "Enabling…" : "Enable"}
              </Button>
            )}
            <Button
              tier="tertiary"
              onClick={() => {
                window.location.href = isAdminShell
                  ? "/admin/notification-preferences"
                  : "/notifications/preferences";
              }}
              data-testid="notifications-preferences-link"
            >
              Email & preferences
            </Button>
          </>
        }
      />

      {/* Push permission blocked → soft card */}
      {pushSupported && permission === "denied" && (
        <Card
          variant="raised"
          className="mb-5 px-4 py-3 flex items-start gap-3 bg-brand-tint/60"
          data-testid="push-blocked-hint"
        >
          <BellOff size={18} className="text-status-urgent shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display font-bold text-ink">
              Notifications are blocked
            </div>
            <div className="text-ink-2 mt-0.5">
              Enable them in <span className="font-mono">Android Settings → Apps → ShelfLife → Notifications</span> so expiration alerts can reach this phone.
            </div>
          </div>
        </Card>
      )}
      {!pushSupported && (
        <Card
          variant="raised"
          className="mb-5 px-4 py-3 text-sm text-ink-2"
          data-testid="push-unsupported"
        >
          Push is not supported on this browser.
        </Card>
      )}

      {/* ---------------- Action row ---------------- */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <button
          type="button"
          onClick={toggleSelectAllVisible}
          disabled={allVisibleIds.length === 0}
          className="text-xs font-display uppercase underline disabled:opacity-30 disabled:no-underline"
          data-testid="select-all-visible-toggle"
        >
          {allVisibleSelected ? "Deselect all visible" : "Select all visible"}
        </button>
        <button
          onClick={markAllRead}
          disabled={busy || unread.length === 0}
          className="text-xs font-display uppercase underline disabled:opacity-30 disabled:no-underline"
          data-testid="mark-all-read-button"
        >
          <CheckCheck size={14} className="inline mr-1" />
          Mark all as read
        </button>
      </div>

      {/* ---------------- Unread section ---------------- */}
      <section className="mb-4" data-testid="unread-section">
        <h2 className="font-display font-bold uppercase text-sm tracking-wider text-ink/80 mb-2">
          Unread {unread.length > 0 && <span className="text-ink/40 font-mono ml-1">· {unread.length}</span>}
        </h2>
        <div className="card-flat">
          {unread.length === 0 ? (
            <div className="p-8 text-center" data-testid="notifications-all-clear">
              <div className="font-display font-bold text-lg">You&apos;re all caught up</div>
              <div className="text-xs text-muted-foreground mt-1">
                {read.length > 0 ? "Open the history below to review past alerts." : "New alerts will appear here."}
              </div>
            </div>
          ) : (
            <>
              {visibleUnread.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  selected={selected.has(n.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
              {hiddenUnread > 0 && (
                <div className="p-3 text-center border-t border-border">
                  <button
                    onClick={() => setShowAllUnread(true)}
                    className="text-xs font-display uppercase underline"
                    data-testid="view-more-unread"
                  >
                    View {hiddenUnread} more unread
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ---------------- Read history ---------------- */}
      {read.length > 0 && (
        <section data-testid="read-history-section">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-expanded={historyOpen}
            aria-controls="read-history-panel"
            className="w-full flex items-center justify-between px-3 py-3 border border-border rounded-sm bg-secondary/40 hover:bg-secondary transition-colors font-display uppercase text-sm tracking-wider"
            data-testid="toggle-read-history"
          >
            <span className="flex items-center gap-2">
              {historyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              Notification history
            </span>
            <span className="font-mono text-xs text-ink/60">{read.length}</span>
          </button>

          {historyOpen && (
            <div id="read-history-panel" className="mt-3 card-flat" data-testid="read-history-panel">
              {visibleRead.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  selected={selected.has(n.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
              {hiddenRead > 0 && (
                <div className="p-3 text-center border-t border-border data-sku">
                  {hiddenRead} older notifications hidden
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ---------------- Selection toolbar (sticky, mobile-friendly) ---------------- */}
      {selectedCount > 0 && (
        <div
          className="fixed left-0 right-0 bottom-[68px] sm:bottom-0 z-40 px-3 py-3 bg-ink text-paper shadow-[0_-4px_16px_rgba(0,0,0,0.18)]"
          role="region"
          aria-label="Notification bulk actions"
          data-testid="selection-toolbar"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="h-11 w-11 flex items-center justify-center rounded-sm hover:bg-paper/10 active:bg-paper/20"
              aria-label="Clear selection"
              data-testid="clear-selection-button"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
            <div className="font-mono text-[12px] uppercase tracking-wider mr-2 shrink-0">
              {selectedCount} selected
            </div>
            <div className="flex-1 flex items-center gap-2 justify-end">
              <button
                onClick={() => setReadForSelected(true)}
                disabled={busy || !selectedHasUnread}
                className="h-11 px-3 rounded-sm bg-paper text-ink font-display text-[12px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform"
                data-testid="bulk-mark-read-button"
              >
                <MailOpen size={16} strokeWidth={2.5} />
                Mark as read
              </button>
              <button
                onClick={() => setReadForSelected(false)}
                disabled={busy || !selectedHasRead}
                className="h-11 px-3 rounded-sm border border-paper text-paper font-display text-[12px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform"
                data-testid="bulk-mark-unread-button"
              >
                <Mail size={16} strokeWidth={2.5} />
                Mark as unread
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
